// components/MultiServicePreviewPage.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Mail,
    Loader2,
    AlertCircle,
    Pencil,
    CheckCircle,
    X,
    Globe,
    Building2,
    IdCard
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import MultiServiceTemplate, { MultiServiceTemplateData } from "@/components/templates/MultiServiceTemplate";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import mainlogo from "@/assets/mylistiq-logo-new.png";
import phoneIcon from "@/assets/telephone_black.png";
import mailIcon from "@/assets/mail_white.png";
import "@/styles/template-responsive.css";

interface Service {
    id: string;
    title: string;
    service_type: string;
    image?: string;
    photos?: string[];
    description?: string;
    phone?: string;
    email?: string;
    website?: string;
    address?: string;
    subject?: string;
    features?: Array<{ id?: number; feature_name: string; name?: string }>;
    [key: string]: any;
}

interface UserProfile {
    id?: string;
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    company?: string;
    company_logo?: string;
    website?: string;
    avatar?: string;
    profile_image?: string;
    license_number?: string;
}

interface EditedServiceData extends Partial<Service> {
    imageFile?: File;
    secondaryImageFile?: File;
}

interface MultiServicePreviewPageProps {
    services: Service[];
    getFullUrl: (url: string | null | undefined) => string;
    selectedTemplateCount?: number;
    onBack: () => void;
    onContinue: () => void;
}

const MultiServicePreviewPage: React.FC<MultiServicePreviewPageProps> = ({
    services: initialServices,
    getFullUrl,
    selectedTemplateCount = 0,
    onBack,
    onContinue,
}) => {
    const { toast } = useToast();

    const [services, setServices] = useState<Service[]>(initialServices);
    const [emailSubject, setEmailSubject] = useState("");
    const [sendingProof, setSendingProof] = useState(false);
    const [proofSentCount, setProofSentCount] = useState<number | null>(null);
    const [isApproved, setIsApproved] = useState(false);

    const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
    const [editedData, setEditedData] = useState<EditedServiceData>({});
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingImageServiceId, setUploadingImageServiceId] = useState<string | null>(null);

    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loadingProfile, setLoadingProfile] = useState(true);

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                setLoadingProfile(true);
                const response = await axiosInstance.get(endpoints.me);
                if (response.data) {
                    setUserProfile({
                        id: response.data.id,
                        email: response.data.email,
                        phone: response.data.phone || response.data.phone_number || response.data.mobile,
                        first_name: response.data.first_name,
                        last_name: response.data.last_name,
                        full_name: response.data.full_name || `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim(),
                        company: response.data.company || response.data.company_name,
                        company_logo: response.data.company_logo || response.data.companyLogo,
                        website: response.data.website || response.data.company_website,
                        avatar: response.data.avatar || response.data.profile_image,
                        license_number: response.data.license_number,
                    });
                }
            } catch (error) {
                console.error("Failed to fetch user profile:", error);
                setUserProfile({ email: "contact@example.com", phone: "", website: "" });
            } finally {
                setLoadingProfile(false);
            }
        };
        fetchUserProfile();
    }, []);

    useEffect(() => {
        setServices(initialServices);
    }, []);

    const companyLogoUrl = userProfile?.company_logo ? getFullUrl(userProfile.company_logo) : mainlogo;

    useEffect(() => {
        try { sessionStorage.setItem("multiServiceEmailSubject", emailSubject); } catch (e) { }
    }, [emailSubject]);

    useEffect(() => {
        try {
            const saved = sessionStorage.getItem("multiServiceEmailSubject");
            if (saved) setEmailSubject(saved);
        } catch (e) { }
    }, []);

    const formatWebsiteDisplay = (website: string | undefined): string => {
        if (!website) return "";
        return website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
    };

    const getWebsiteUrl = (website: string | undefined): string => {
        if (!website) return "#";
        if (website.startsWith("http://") || website.startsWith("https://")) return website;
        return `https://${website}`;
    };

    const normalizeUrl = (url?: string | null): string => {
        const trimmed = (url || "").trim();
        if (!trimmed) return "";
        return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    };

    const getFirstApiError = (data: any): string | null => {
        if (!data) return null;
        if (typeof data === "string") return data;
        if (typeof data?.error === "string") return data.error;
        if (typeof data?.message === "string") return data.message;
        if (typeof data === "object") {
            const firstKey = Object.keys(data)[0];
            const v = (data as any)[firstKey];
            if (Array.isArray(v) && v[0]) return `${firstKey}: ${v[0]}`;
            if (typeof v === "string") return `${firstKey}: ${v}`;
        }
        return null;
    };

    const handleStartEdit = (index: number) => {
        setEditingServiceIndex(index);
        setEditedData({});
    };

    const handleCancelEdit = () => {
        setEditingServiceIndex(null);
        setEditedData({});
    };

    const handleServiceChange = useCallback((fields: Partial<MultiServiceTemplateData>) => {
        setEditedData(prev => {
            const updates: Partial<Service> = { ...prev };
            if (fields.title !== undefined) updates.title = fields.title;
            if (fields.service_type !== undefined) updates.service_type = fields.service_type;
            if (fields.description !== undefined) updates.description = fields.description;
            if (fields.phone !== undefined) updates.phone = fields.phone;
            if (fields.email !== undefined) updates.email = fields.email;
            if (fields.website !== undefined) updates.website = fields.website;
            if (fields.address !== undefined) updates.address = fields.address;
            if (fields.image !== undefined) updates.image = fields.image;
            if (fields.features !== undefined) updates.features = fields.features;
            return updates;
        });
    }, []);

    const handleImageChange = useCallback((serviceId: string, file: File, type: 'main' | 'secondary') => {
        const previewUrl = URL.createObjectURL(file);
        setEditedData(prev => {
            const updated: EditedServiceData = { ...prev };
            if (type === 'main') {
                updated.imageFile = file;
                updated.image = previewUrl;
            } else {
                updated.secondaryImageFile = file;
                updated.photos = [previewUrl, ...(prev.photos?.slice(1) || [])];
            }
            return updated;
        });
    }, []);

    const handleSaveEdit = async () => {
        if (editingServiceIndex === null) return;
        setIsSaving(true);
        try {
            const service = services[editingServiceIndex];
            const updates: any = { ...editedData };

            (["title", "service_type", "description", "phone", "email", "address", "subject"] as const).forEach((k) => {
                if (typeof updates[k] === "string") updates[k] = updates[k].trim();
            });

            if (updates.website !== undefined) updates.website = normalizeUrl(updates.website);

            if (updates.title !== undefined && !updates.title) {
                toast({ title: "Missing Title", description: "Service title is required.", variant: "destructive", duration: 1000 });
                return;
            }
            if (updates.service_type !== undefined && !updates.service_type) {
                toast({ title: "Missing Service Type", description: "Service type is required.", variant: "destructive", duration: 1000 });
                return;
            }
            if (updates.description !== undefined && !updates.description) {
                toast({ title: "Missing Description", description: "Description is required.", variant: "destructive", duration: 1000 });
                return;
            }

            if (updates.features) {
                updates.features = updates.features
                    .filter((f: any) => (f.feature_name || f.name || "").toString().trim().length > 0)
                    .map((f: any) => ({ id: f.id, name: (f.feature_name || f.name || "").toString().trim() }));
            }

            const hasImageFile = editedData.imageFile instanceof File;
            const hasSecondaryImageFile = editedData.secondaryImageFile instanceof File;
            let response;

            if (hasImageFile || hasSecondaryImageFile) {
                const formData = new FormData();
                if (updates.title !== undefined) formData.append('title', updates.title || '');
                if (updates.service_type !== undefined) formData.append('service_type', updates.service_type || '');
                if (updates.description !== undefined) formData.append('description', updates.description || '');
                if (updates.phone !== undefined) formData.append('phone', updates.phone || '');
                if (updates.email !== undefined) formData.append('email', updates.email || '');
                if (updates.address !== undefined) formData.append('address', updates.address || '');
                if (updates.website !== undefined) formData.append('website', normalizeUrl(updates.website));
                if (updates.subject !== undefined) formData.append('subject', updates.subject || '');
                if (hasImageFile && editedData.imageFile) {
                    formData.append('hero_image', editedData.imageFile);
                    // A freshly-picked main photo becomes its own untouched
                    // "original" too, so Edit Image (MultiServiceTemplate.tsx)
                    // always re-crops from this new photo instead of a stale
                    // original left over from before this replacement.
                    formData.append('original_hero_image', editedData.imageFile);
                }
                if (hasSecondaryImageFile && editedData.secondaryImageFile) formData.append('photo_1', editedData.secondaryImageFile);

                response = await axiosInstance.patch(
                    `${endpoints.service_listings}${service.id}/`,
                    formData,
                    { headers: { "Content-Type": undefined as any }, transformRequest: [(data) => data] }
                );
            } else {
                response = await axiosInstance.patch(`${endpoints.service_listings}${service.id}/`, updates);
            }

            if (response.data) {
                const fullResponse = await axiosInstance.get(`${endpoints.service_listings}${service.id}/`);
                setServices(prev => {
                    const updated = [...prev];
                    const heroUrl =
                        fullResponse.data.hero_image_url ||
                        fullResponse.data.hero_image ||
                        fullResponse.data.image ||
                        "";
                    updated[editingServiceIndex] = {
                        ...updated[editingServiceIndex],
                        ...fullResponse.data,
                        image: heroUrl ? getFullUrl(heroUrl) : updated[editingServiceIndex].image,
                        photos: Array.isArray(fullResponse.data.photos) && fullResponse.data.photos.length > 0
                            ? fullResponse.data.photos.map((url: string) => getFullUrl(url))
                            : updated[editingServiceIndex].photos,
                        features: fullResponse.data.features || updated[editingServiceIndex].features,
                    };
                    return updated;
                });
                toast({ title: "Changes Saved", description: "Your service has been updated successfully.", duration: 1000 });
                setEditingServiceIndex(null);
                setEditedData({});
            }
        } catch (error) {
            console.error("Failed to save:", error);
            const serverMsg = getFirstApiError((error as any)?.response?.data);
            toast({ title: "Save Failed", description: serverMsg || "Could not save changes. Please try again.", variant: "destructive", duration: 1000 });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendProof = async () => {
        if (!emailSubject?.trim()) {
            toast({ title: "Subject Required", description: "Please enter an email subject before sending proof.", variant: "destructive", duration: 1000 });
            return;
        }
        if (editingServiceIndex !== null) {
            toast({ title: "Save Changes First", description: "Please save or cancel your edits before sending proof.", variant: "destructive", duration: 1000 });
            return;
        }

        const hasValidFeatures = services.every((service) => {
            const features = service.features || [];
            return features.some((feature: any) => (feature.feature_name || feature.name || feature || "").toString().trim().length > 0);
        });

        if (!hasValidFeatures) {
            toast({ title: "Add at least one feature", description: "Each service must include at least one feature before sending proof.", variant: "destructive", duration: 1000 });
            return;
        }

        setSendingProof(true);
        try {
            const meRes = await axiosInstance.get(endpoints.me);
            const userEmail = meRes?.data?.email;
            if (!userEmail) {
                toast({ title: "No Email Found", description: "Could not retrieve your email address.", variant: "destructive", duration: 1000 });
                setSendingProof(false);
                return;
            }

            const mappedServices = services.map((s) => ({
                id: s.id,
                title: s.title || "",
                service_type: s.service_type || s.serviceType || "",
                description: s.description || "",
                phone: s.phone || "",
                email: s.email || "",
                website: s.website || "",
                address: s.address || "",
                image: s.image ? getFullUrl(s.image) : "",
                photos: s.photos ? s.photos.map((p: string) => getFullUrl(p)) : [],
                features: (s.features || []).map((f: any) => f.feature_name || f.name || f || ""),
            }));

            const payload = {
                campaign_type: "multi_service",
                template_path: "multiproperty/service/template.html",
                subject: emailSubject,
                user_email: userEmail,
                template_count: services.length,
                services: mappedServices,
            };

            await axiosInstance.post(endpoints.email_proof || "campaign/email/send-proof/", payload);
            setProofSentCount(prev => (prev === null ? 1 : prev + 1));
            setIsApproved(false);
            toast({ title: "Proof Sent!", description: `Preview email sent to ${userEmail} with ${services.length} services.`, duration: 1000 });
        } catch (error: any) {
            console.error("Failed to send proof:", error);
            const serverMsg = error?.response?.data?.error || error?.response?.data?.message || null;
            toast({ title: "Failed to send proof", description: serverMsg || "Please try again.", variant: "destructive", duration: 1000 });
        } finally {
            setSendingProof(false);
        }
    };

    const handleContinue = () => {
        if (!emailSubject.trim()) {
            toast({ title: "Subject Required", description: "Please enter an email subject before continuing.", variant: "destructive", duration: 1000 });
            return;
        }
        if (proofSentCount === null) {
            toast({ title: "Proof Required", description: "Please send and review the proof email before continuing.", variant: "destructive", duration: 1000 });
            return;
        }
        if (!isApproved) {
            toast({ title: "Approval Required", description: "Please confirm you have reviewed and approved the proof.", variant: "destructive", duration: 1000 });
            return;
        }
        if (editingServiceIndex !== null) {
            toast({ title: "Save Changes", description: "Please save or cancel your edits before continuing.", variant: "destructive", duration: 1000 });
            return;
        }
        try {
            sessionStorage.setItem("multiSelectedServices", JSON.stringify(services));
            sessionStorage.setItem("multiServiceEmailSubject", emailSubject);
        } catch (e) { }
        onContinue();
    };

    const getServiceWithEdits = (index: number): Service => {
        const service = services[index];
        if (index === editingServiceIndex) {
            const { imageFile, secondaryImageFile, ...safeEdits } = editedData;
            return { ...service, ...safeEdits };
        }
        return service;
    };

    const renderServiceTemplate = (service: Service, index: number) => {
        const isEditing = editingServiceIndex === index;
        return (
            <MultiServiceTemplate
                key={service.id}
                index={index + 1}
                service={{
                    id: service.id,
                    title: service.title || "",
                    service_type: service.service_type || "",
                    description: service.description || "",
                    phone: service.phone || "",
                    email: service.email || "",
                    website: service.website || "",
                    address: service.address || "",
                    image: service.image ? getFullUrl(service.image) : "",
                    photos: service.photos ? service.photos.map(url => getFullUrl(url)) : [],
                    features: service.features || [],
                }}
                getFullUrl={getFullUrl}
                readOnly={!isEditing}
                imageUploading={uploadingImageServiceId === service.id}
                onChange={isEditing ? handleServiceChange : undefined}
                onImageChange={isEditing ? (file: File, type: 'main' | 'secondary') => {
                    handleImageChange(service.id, file, type);
                } : undefined}
                onContact={() => toast({ title: "Contact", description: `Contact for ${service.title}`, duration: 1000 })}
            />
        );
    };

    // ─── Inline responsive styles ────────────────────────────────────────────
    const headerCellStyle = (align: 'left' | 'right'): React.CSSProperties => ({
        verticalAlign: 'middle',
        width: '50%',
        textAlign: align,
    });

    return (
        <div className="template-preview-page template-bottom-safe w-full max-w-[1400px] mx-auto">
            {/* ── Page heading ─────────────────────────────────────── */}
            <div className="text-center mb-6 sm:mb-8">
                <h2 className="page-title text-primary mb-2">
                    Preview Multi-Service Launch
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base">
                    Review your {services.length} service{services.length !== 1 ? 's' : ''} before sending
                </p>
            </div>

            {/* ── Email subject card ────────────────────────────────── */}
            <Card className="mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                    {/* Stack on mobile, row on sm+ */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 flex-wrap">
                        <div className="flex-1 w-full sm:max-w-xl">
                            <h3 className="text-base sm:text-lg font-semibold mb-2">Email Subject</h3>
                            <Input
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                placeholder="Enter email subject"
                                className="text-sm sm:text-base w-full"
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Badge variant="secondary" className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 mt-9">
                                {services.length} Service{services.length !== 1 ? 's' : ''}
                            </Badge>
                            {selectedTemplateCount > 0 && (
                                <Badge variant="secondary" className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2">
                                    {selectedTemplateCount} Template{selectedTemplateCount !== 1 ? 's' : ''}
                                </Badge>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Read-only notice ─────────────────────────────────── */}
            {editingServiceIndex === null && (
                <div className="template-preview-banner mb-4 sm:mb-6 rounded-lg border border-blue-200 bg-blue-50">
                    <p className="text-sm text-blue-800 flex flex-wrap items-start gap-1.5 p-4">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>
                            This is a read-only preview. Click{" "}
                            <strong>&quot;Edit Template&quot;</strong> to make changes.
                        </span>
                    </p>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════
                EMAIL PREVIEW CONTAINER
                ═══════════════════════════════════════════════════════ */}
            <div className="mb-4 sm:mb-6 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: "touch" }}>
                <Card className="template-preview-frame inline-block min-w-[760px] w-fit max-w-none align-top">

                    {/* ── Email header (agent info) ─────────────────────── */}
                    <div
                        style={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        }}
                        className="px-4 py-3 sm:px-6 sm:py-4"
                    >
                        {loadingProfile ? (
                            <div className="text-center">
                                <Loader2 className="h-6 w-6 text-white animate-spin mx-auto" />
                            </div>
                        ) : (
                            /* ── Responsive header: flex-col on mobile, row on sm ── */
                            <div className="flex items-center justify-between gap-4 sm:gap-6  p-2">

                                {/* Left: avatar + name + contact info */}
                                <div className="flex min-w-0 items-center gap-3 sm:gap-4" >
                                    {userProfile?.avatar ? (
                                        <img
                                            src={getFullUrl(userProfile.avatar)}
                                            alt={userProfile.full_name || "User"}
                                            className="block h-[120px] w-[90px] rounded-xl object-cover shadow-sm flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="flex h-[90px] w-[90px] rounded-xl flex-shrink-0 items-center justify-center border-2 border-white/40 bg-white/15 shadow-sm">
                                            <span className="text-lg font-semibold text-white">
                                                {(userProfile?.first_name?.[0] || userProfile?.email?.[0] || 'U').toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="truncate text-base font-semibold leading-tight text-white sm:text-lg">
                                            {userProfile?.full_name || `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Real Estate Agent'}
                                        </p>
                                        <div className="flex flex-col gap-1 text-sm leading-5 text-white/90">
                                            {userProfile?.phone && (
                                                <span className="inline-flex items-center gap-2 truncate">
                                                    <img src={phoneIcon} alt="Phone" className="h-3.5 w-3.5 flex-shrink-0 brightness-0 invert" />
                                                    <span className="truncate">{userProfile.phone}</span>
                                                </span>
                                            )}
                                            {userProfile?.email && (
                                                <span className="inline-flex items-center gap-2 truncate">
                                                    <img src={mailIcon} alt="Email" className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate underline">
                                                        {/* {userProfile.email} */}
                                                         Click here to email Me
                                                    </span>
                                                </span>
                                            )}
                                            {userProfile?.company && (
                                                <span className="inline-flex items-center gap-2 truncate text-blue-100">
                                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate">{userProfile.company}</span>
                                                </span>
                                            )}
                                            {userProfile?.license_number && (
                                                <span className="inline-flex items-center gap-2 truncate text-blue-100">
                                                    <IdCard className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate">License #: {userProfile.license_number}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: company logo rectangle */}
                                <div className="flex items-center justify-center flex-shrink-0">
                                    <div className="h-[120px] w-[120px] overflow-hidden rounded-xl shadow-sm flex items-center justify-center">
                                        <img src={companyLogoUrl} alt="Company Logo" className="block h-full w-full rounded-xl object-contain" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Content area ─────────────────────────────────── */}
                    <CardContent className="template-preview-shell bg-slate-50 p-0">
                        <div className="space-y-6 sm:space-y-8 m-0">
                            {services.map((service, index) => {
                                const isEditing = editingServiceIndex === index;
                                const displayService = getServiceWithEdits(index);

                                return (
                                    <div key={service.id} className="relative w-full ">
                                        {index > 0 && (
                                            <hr className="border-t-2 border-gray-200 mb-6 sm:mb-8" />
                                        )}
                                        {/* Per-service toolbar */}
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3 mx-6 mt-3">
                                            <h4 className="font-medium text-gray-700 text-sm sm:text-base flex items-center gap-2">
                                                {isEditing && (
                                                    <Badge className="bg-amber-500 text-xs">Editing</Badge>
                                                )}
                                            </h4>

                                            {isEditing ? (
                                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center w-full sm:w-auto">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={handleCancelEdit}
                                                        disabled={isSaving}
                                                        className="w-full sm:w-auto"
                                                    >
                                                        <X className="h-4 w-4 mr-1" />Cancel
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={handleSaveEdit}
                                                        disabled={isSaving}
                                                        className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                                                    >
                                                        {isSaving ? (
                                                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</>
                                                        ) : (
                                                            <><CheckCircle className="h-4 w-4 mr-1" />Save Changes</>
                                                        )}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleStartEdit(index)}
                                                    disabled={editingServiceIndex !== null && editingServiceIndex !== index}
                                                    className="w-full sm:w-auto"
                                                >
                                                    <Pencil className="h-4 w-4 mr-1" />Edit Template
                                                </Button>
                                            )}
                                        </div>

                                        {/* Edit mode banner */}
                                        {isEditing && (
                                            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 mx-6">
                                                <p className="text-sm text-amber-800 flex items-start gap-2 flex-wrap">
                                                    <Pencil className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                    <span>
                                                        <strong>Edit Mode:</strong> Click on any field to edit it directly.
                                                        Don&apos;t forget to save your changes!
                                                    </span>
                                                </p>
                                            </div>
                                        )}

                                        {/* Template slot — clips overflow */}
                                        <div
                                            className={`template-slot-wrapper template-slot-wrapper--service w-full rounded-lg ${!isEditing && editingServiceIndex !== null ? 'template-slot-dimmed' : ''}`}
                                        >
                                            {renderServiceTemplate(displayService, index)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>

                    {/* ── Email footer (logo + count) ───────────────────── */}
                    <div
                        style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}
                        className="p-4 sm:px-8 sm:py-6 lg:px-12 lg:py-7"
                    >
                        {/* Responsive: flex-col on mobile, row on sm */}
                        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between sm:gap-4 p-4">

                            {/* Logo + copyright */}
                            <div className="flex items-center gap-4">
                                <a
                                    href="https://mylistiq.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-shrink-0"
                                >
                                    <div
                                        className="flex items-center justify-center rounded-2xl bg-white"
                                        style={{ width: '56px', height: '56px' }}
                                    >
                                        <img
                                            src={mainlogo}
                                            alt="MyListIQ"
                                            style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                                        />
                                    </div>
                                </a>
                                <span className="text-white/85 text-xs sm:text-sm leading-snug">
                                    © {new Date().getFullYear()} MyListIQ.<br className="sm:hidden" /> All rights reserved.
                                </span>
                            </div>

                            {/* Service count pill */}
                            <span
                                className="text-white text-xs sm:text-sm font-semibold px-4 py-2 rounded-full"
                                style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                            >
                                {services.length} {services.length === 1 ? 'Service' : 'Services'} Available
                            </span>
                        </div>
                    </div>

                </Card>
            </div>
            {/* ═══════ END EMAIL PREVIEW ═══════ */}

            {/* {editingServiceIndex === null && (
                <div className="template-preview-banner mb-4 sm:mb-6 rounded-lg border border-blue-200 bg-blue-50">
                    <p className="text-sm text-blue-800 flex flex-wrap items-start gap-1.5 p-4">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>
                            This is a read-only preview. Click{" "}
                            <strong>&quot;Edit Template&quot;</strong> to make changes.
                        </span>
                    </p>
                </div>
            )} */}


            {/* ── Send proof card ────────────────────────────────── */}
            <Card className="template-preview-card border-blue-200 bg-blue-50/50 mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                    <div className="template-proof-row flex gap-3 sm:gap-4">
                        <div className="template-proof-icon w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Mail className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-1">
                                Send Proof Email
                            </h3>
                            <p className="text-xs sm:text-sm text-blue-800 mb-4">
                                Preview how your multi-service email will look before sending.
                            </p>
                            <Button
                                size="sm"
                                onClick={handleSendProof}
                                disabled={sendingProof || editingServiceIndex !== null}
                                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                            >
                                {sendingProof ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : proofSentCount !== null ? (
                                    <>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Resend Proof
                                    </>
                                ) : (
                                    <>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Send Proof
                                    </>
                                )}
                            </Button>

                            {editingServiceIndex !== null && (
                                <p className="text-xs sm:text-sm text-amber-600 mt-2">
                                    Please save or cancel your edits before sending proof.
                                </p>
                            )}

                            {proofSentCount !== null && editingServiceIndex === null && (
                                <div className="mt-3 flex items-center gap-2 text-xs sm:text-sm text-green-700">
                                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                                    <span>Proof email sent. Check your inbox.</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Approval card ──────────────────────────────────── */}
            <Card className="template-preview-card border-amber-300 bg-amber-50/70 mb-4 sm:mb-6">
                <CardContent className="pt-4 sm:pt-6">
                    <label className="flex items-start gap-3 sm:gap-4 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={isApproved}
                            onChange={(e) => setIsApproved(e.target.checked)}
                            disabled={proofSentCount === null || editingServiceIndex !== null}
                            className="mt-1 w-5 h-5 rounded text-amber-600 flex-shrink-0"
                        />
                        <div className="min-w-0">
                            <p className="text-base sm:text-lg font-semibold text-amber-900 mb-1">
                                I have reviewed and approve this as final
                            </p>
                            <p className="text-xs sm:text-sm text-amber-800">
                                By checking this box, you confirm that all {services.length} service{services.length !== 1 ? 's' : ''}{' '}
                                are correct and ready to be sent.
                            </p>
                        </div>
                    </label>
                </CardContent>
            </Card>

            {/* ── Navigation footer ──────────────────────────────── */}
            <div className="template-footer-actions">
                <Button
                    size="lg"
                    variant="outline"
                    onClick={onBack}
                    disabled={editingServiceIndex !== null || isSaving}
                    className="w-full sm:w-auto"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Back
                </Button>

                <Button
                    size="lg"
                    onClick={handleContinue}
                    disabled={!(proofSentCount !== null && isApproved) || editingServiceIndex !== null}
                    className={`w-full sm:w-auto bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 text-white
                        ${!(proofSentCount !== null && isApproved) || editingServiceIndex !== null
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:from-blue-500 hover:via-blue-600 hover:to-blue-700"
                        }`}
                >
                    Looks Good – Continue
                    <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
            </div>
        </div>
    );
};

export default MultiServicePreviewPage;
